import { memo, useCallback } from 'react';
import { StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import { SharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CloseIcon } from '@/assets/icons/close';
import { InfoWhiteIcon } from '@/assets/icons/info-white';
import { Text } from '@/components/TextComponent';
import { SubmissionOverlay } from '@/components/checkout/SubmissionOverlay';
import { Box } from '@/components/lib/components/Box';
import { Row } from '@/components/lib/components/Row';
import { ds } from '@/components/lib/design.system';
import { Handle } from '@/feature/alerts/components/Modal';
import { designSystem } from '@/styles/styles';
import { RaceToPlaceEntry } from '@/types/socialFeed.generated';
import { MAX_WEB_WIDTH, MODAL_PADDINGS, isWeb } from '@/utils/constants-platform-specific';

import { RaceToPlaceModal } from './RaceToPlaceModal';
import { StackedCards } from './StackedCards';
import { useRaceToPlaceInfoSheet } from './hooks/use-race-to-place-info-sheet';
import { RACE_TO_PLACE_MODAL_BASE_HEIGHT } from './hooks/use-race-to-place-modal-height';
import { useRaceToPlaceStore } from './hooks/use-race-to-place-store';
import { RACE_TO_PLACE_MODAL_MARGIN_TOP, RACE_TO_PLACE_MODAL_SHEET_TITLE_HEIGHT } from './race-to-place-constants';

export const RaceToPlaceSheet = memo(
    ({
        handleClosePress,
        showLoadingOverlay,
        setShowLoadingOverlay,
    }: {
        handleClosePress: () => void;
        showLoadingOverlay: boolean;
        setShowLoadingOverlay: (show: boolean) => void;
    }) => {
        const showRaceToPlaceInfoSheet = useRaceToPlaceInfoSheet();
        const data = useRaceToPlaceStore(state => state.raceToPlaceData);

        const renderCard = useCallback(
            (entry: RaceToPlaceEntry, cardHeight: SharedValue<number>) => (
                <RaceToPlaceModal
                    raceToPlaceEntry={entry}
                    cardHeight={cardHeight}
                    setShowLoadingOverlay={setShowLoadingOverlay}
                />
            ),
            [setShowLoadingOverlay]
        );

        const { width } = useWindowDimensions();
        const insets = useSafeAreaInsets();

        return (
            <Box
                width={isWeb ? (MAX_WEB_WIDTH > width ? width : MAX_WEB_WIDTH) - MODAL_PADDINGS : undefined}
                justifyContent={'flex-end'}
                overflow={'hidden'}
                testID={'race-to-place-sheet'}
            >
                <Handle handleContainerStyle={styles.handleContainer} handleColor={designSystem.colors.neutral500} />
                <Box
                    pt={isWeb ? 's16' : 's0'}
                    pb={'s16'}
                    px={'s16'}
                    borderRadius={isWeb ? 'r24' : undefined}
                    overflow={'hidden'}
                    style={{ paddingBottom: insets.bottom + 16 }}
                    backgroundColor={ds.colorToken.background.neutral.base}
                >
                    <Row
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        height={RACE_TO_PLACE_MODAL_SHEET_TITLE_HEIGHT}
                    >
                        <TouchableOpacity onPress={handleClosePress}>
                            <CloseIcon color={ds.colorValue.icon.primary} />
                        </TouchableOpacity>
                        <Text variant={'titleMedium2'}>Race to Place</Text>
                        <TouchableOpacity onPress={showRaceToPlaceInfoSheet}>
                            <InfoWhiteIcon />
                        </TouchableOpacity>
                    </Row>
                    <Box overflow={'hidden'}>
                        <StackedCards
                            containerStyle={styles.stackedCards}
                            data={data}
                            placeholderHeight={RACE_TO_PLACE_MODAL_BASE_HEIGHT}
                            renderCard={renderCard}
                        />
                    </Box>
                </Box>
                {showLoadingOverlay ? <SubmissionOverlay /> : null}
            </Box>
        );
    }
);

const styles = StyleSheet.create({
    handleContainer: {
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    stackedCards: {
        marginTop: RACE_TO_PLACE_MODAL_MARGIN_TOP,
    },
});
